import { useMemo } from 'react'

import { usePrincipal } from '../../../../application/authentication'
import { isInputConfig } from '../../../../application/formBuilder/config'
import { useProposal } from '../../../../application/product/proposal/ProposalProvider'
import { Data, PolicyChangeType, Profile, Proposal } from '../../../../domain'
import { useAdjustments } from '../../../../state'
import { FormConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { useErrorHandlingAsyncCallback } from '../../../errors'
import { hasChanges } from '../helpers'
import { useAdditionalProfileProposalAdjustmentRequest } from './adjustments'
import { toAdditionalProfileProposalData } from './data'

export const useAdditionalProfileFormData = (
  form: FormConfig,
  profile: Profile | undefined,
) => {
  const { getAdjustment } = useAdjustments()

  return useMemo(() => {
    if (!profile) return {}

    const toData = toAdditionalProfileProposalData(profile)

    return form.filter(isInputConfig).reduce((data, input) => {
      const adjustment = getAdjustment(input.name)

      return {
        ...data,
        [input.name]: toData(adjustment),
      }
    }, {})
  }, [form, profile, getAdjustment])
}

export const useRemoveAdditionalProfileProposalAdjustment = (applyRating: boolean) => {
  const { proposal, setProposal } = useProposal()
  const { apiClient } = usePrincipal()

  return useErrorHandlingAsyncCallback(async (profile: Profile) => {
    setProposal(
      await apiClient.updateProposal(proposal.proposalCode, {
        applyRating,
        changes: [
          {
            changeType: PolicyChangeType.REMOVE_PROFILE,
            profileCode: profile.profileCode,
          },
        ],
      }),
    )
  })
}

interface AdditionalProfileProposalAdjustmentProps {
  form: FormConfig
  applyRating: boolean
  profile: Profile
  onComplete?: (proposal: Proposal) => void | Promise<void>
}

export const useAdditionalProfileProposalAdjustment = ({
  form,
  applyRating,
  profile,
  onComplete,
}: AdditionalProfileProposalAdjustmentProps) => {
  const { apiClient } = usePrincipal()
  const { proposal, setProposal } = useProposal()

  const existingData = useAdditionalProfileFormData(form, profile)
  const proposalAdjustmentRequest = useAdditionalProfileProposalAdjustmentRequest(
    form,
    applyRating,
    profile,
  )

  return useApiErrorHandlingAsyncCallback(async (data: Data) => {
    const adjustment = proposalAdjustmentRequest(existingData, data)

    const updatedProposal = hasChanges(adjustment)
      ? await apiClient.updateProposal(proposal.proposalCode, adjustment)
      : proposal

    setProposal(updatedProposal)
    await onComplete?.(updatedProposal)
  })
}
